import { FormEvent, useCallback, useState } from 'react';
import { useEffectOnce } from 'react-use';
import { DirectCampaignUI } from '../../../../data-layer/direct/types';
import { loadGeoSuggestsAsync } from '../../../../data-layer/geo/actions';
import { selectGeo } from '../../../../data-layer/geo/selectors';
import { useDispatch } from '../../../../data-layer/useDispatch';
import { useSelector } from '../../../../data-layer/useSelector';
import { DEFAULT_GEO, DEFAULT_REGION_ID } from '../constants';

function getRegionsSettings(company?: DirectCampaignUI) {
    const { regions } = company || {};

    if (!regions?.length) {
        return DEFAULT_GEO;
    }

    return regions.reduce<Record<string, string>>(
        (res, region) => ({
            ...res,
            [String(region.region_id)]: region.name,
        }),
        {},
    );
}

export const usePromotionGeo = (company?: DirectCampaignUI) => {
    const dispatch = useDispatch();
    const geo = useSelector((state) => selectGeo(state));
    const [regionQuery, setRegionQuery] = useState('');
    const onLoadGeoSuggests = useCallback(
        (query?: string) => dispatch(loadGeoSuggestsAsync(query)),
        [dispatch],
    );

    const [selectedRegions, setSelectedRegions] = useState<Record<string, string>>(
        getRegionsSettings(company),
    );

    const onSelectRegion = useCallback(
        (value: Record<string, string>) => {
            setSelectedRegions({ ...selectedRegions, ...value });
            setRegionQuery('');
        },
        [selectedRegions],
    );

    const onRemoveRegion = useCallback(
        (key: string) => {
            const newSelectedRegions = { ...selectedRegions };

            delete newSelectedRegions[key];

            setSelectedRegions(newSelectedRegions);
        },
        [selectedRegions],
    );

    const onInputRegion = useCallback(
        (event: FormEvent<HTMLInputElement>) => {
            const value = event.currentTarget.value;

            if (!geo.isLoading) {
                setRegionQuery(value);
                onLoadGeoSuggests(value);
            }
        },
        [geo.isLoading, onLoadGeoSuggests],
    );

    useEffectOnce(() => {
        onLoadGeoSuggests(DEFAULT_GEO[DEFAULT_REGION_ID]);
    });

    return {
        selectedRegions,
        geoSuggests: geo.suggests,
        regionQuery,
        onSelectRegion,
        onRemoveRegion,
        onInputRegion,
    };
};
